import React, { useMemo, useCallback } from "react";
import { Transaction } from "../types";

interface MonthlyExpensesCalendarProps {
  transactions: Transaction[];
  selectedMonth: number;
  currentYear: number;
  loading?: boolean;
  error?: string | null;
}

const MonthlyExpensesCalendar: React.FC<MonthlyExpensesCalendarProps> = ({
  transactions,
  selectedMonth,
  currentYear,
  loading = false,
  error = null,
}) => {
  // 格式化金额显示
  const formatAmount = useCallback((amount: number): string => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000) {
      return (absAmount / 1000).toFixed(2) + 'k';
    } else {
      return Math.round(absAmount).toString();
    }
  }, []);
  // 计算每日支出数据 - 使用 useMemo 优化性能
  const dailyExpenses = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {};
    }

    const expenses: { [day: number]: number } = {};

    try {
      transactions.forEach((transaction) => {
        if (!transaction.date || typeof transaction.amount !== "number") {
          return;
        }

        // 处理日期格式
        let transactionDate: Date;
        if (transaction.date.includes("T")) {
          transactionDate = new Date(transaction.date);
        } else if (transaction.date.includes("-")) {
          const dateParts = transaction.date.split(/[-/]/);
          if (dateParts.length === 3) {
            transactionDate = new Date(
              parseInt(dateParts[0]),
              parseInt(dateParts[1]) - 1,
              parseInt(dateParts[2])
            );
          } else {
            transactionDate = new Date(transaction.date);
          }
        } else {
          transactionDate = new Date(transaction.date);
        }

        if (isNaN(transactionDate.getTime())) {
          return;
        }

        const transactionMonth = transactionDate.getMonth();
        const transactionYear = transactionDate.getFullYear();

        if (
          transactionMonth === selectedMonth &&
          transactionYear === currentYear
        ) {
          const day = transactionDate.getDate();

          // 只计算支出（负数或支出类别的交易）
          if (transaction.amount < 0) {
            // 负数金额直接累加（保持负数）
            expenses[day] = (expenses[day] || 0) + transaction.amount;
          } else if (transaction.amount > 0) {
            // 正数金额，检查是否为支出类别
            const incomeCategories = ["工资", "奖金", "投资收益", "其他收入"];
            const isIncome = incomeCategories.some(
              (cat) =>
                transaction.category?.includes(cat) ||
                transaction.description?.includes(cat)
            );

            if (!isIncome) {
              // 非收入类别的正数金额转为负数
              expenses[day] = (expenses[day] || 0) - transaction.amount;
            }
          }
        }
      });
    } catch (err) {
      console.error("Error calculating daily expenses:", err);
      return {};
    }

    return expenses;
  }, [transactions, selectedMonth, currentYear]);

  // 获取当月的天数和第一天是星期几 - 使用 useCallback 优化性能
  const getDaysInMonth = useCallback((month: number, year: number) => {
    try {
      return new Date(year, month + 1, 0).getDate();
    } catch (err) {
      console.error("Error getting days in month:", err);
      return 31; // 默认返回31天
    }
  }, []);

  const getFirstDayOfWeek = useCallback((month: number, year: number) => {
    try {
      // 获取月份第一天是星期几 (0 = 周日, 1 = 周一, ..., 6 = 周六)
      // 保持周日开始的布局
      return new Date(year, month, 1).getDay();
    } catch (err) {
      console.error("Error getting first day of week:", err);
      return 0; // 默认返回周日
    }
  }, []);

  const daysInMonth = getDaysInMonth(selectedMonth, currentYear);
  const firstDayOfWeek = getFirstDayOfWeek(selectedMonth, currentYear);
  const today = new Date();
  const isCurrentMonth =
    today.getMonth() === selectedMonth && today.getFullYear() === currentYear;
  const todayDate = isCurrentMonth ? today.getDate() : -1;

  // 生成日历网格 - 使用 useMemo 优化性能
  const calendarDays = useMemo(() => {
    const days = [];

    // 添加空白天数（上个月的尾部）
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    // 添加当月的天数
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [firstDayOfWeek, daysInMonth]);

  // 月份名称（中文）
  const monthNames = [
    "一月",
    "二月",
    "三月",
    "四月",
    "五月",
    "六月",
    "七月",
    "八月",
    "九月",
    "十月",
    "十一月",
    "十二月",
  ];

  // 星期名称（中文，周一开始）
  const weekDays = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

  return (
    <div className="bg-gray-50 rounded-lg sm:p-3">
      {/* 日历标题 */}
      <div className="text-center mb-2">
        <h4 className="text-sm sm:text-base font-medium text-gray-800">
          {monthNames[selectedMonth]} {currentYear}
        </h4>

        {/* 统计信息 - 移到标题下方 */}
        <div className="text-xs text-gray-500 mt-1">
          交易:{" "}
          {
            Object.keys(dailyExpenses).filter(
              (day) => dailyExpenses[parseInt(day)] !== 0
            ).length
          }{" "}
          | 支出天数: {Object.keys(dailyExpenses).length}
        </div>

        {/* 加载和错误状态 */}
        {loading && (
          <div className="flex justify-center items-center mt-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
            <span className="ml-1 text-xs text-gray-500">加载中...</span>
          </div>
        )}

        {error && (
          <div className="mt-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
            {error}
          </div>
        )}
      </div>

      {/* 星期标题 */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-gray-400 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* 日历网格 */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {calendarDays.map((day, index) => {
          if (day === null) {
            // 空白天数 - 显示上个月的日期
            const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
            const prevYear =
              selectedMonth === 0 ? currentYear - 1 : currentYear;
            const prevMonthDays = getDaysInMonth(prevMonth, prevYear);
            const prevDay = prevMonthDays - (firstDayOfWeek - index - 1);

            return (
              <div
                key={`prev-${index}`}
                className="bg-white rounded-sm p-1 h-12 sm:h-16 flex flex-col justify-center items-center text-center border border-gray-100"
              >
                <div className="text-xs sm:text-sm font-medium text-gray-300 leading-none">
                  {prevDay}
                </div>
              </div>
            );
          }

          const dayExpense = dailyExpenses[day] || 0;
          const isToday = day === todayDate;
          const hasExpense = dayExpense !== 0;

          return (
            <div
              key={day}
              className={`bg-white rounded-sm p-1 h-12 sm:h-16 flex flex-col justify-center items-center text-center transition-all duration-200 hover:shadow-sm border border-gray-100 ${
                isToday ? "ring-1 ring-blue-400 bg-blue-50" : ""
              }`}
            >
              {/* 日期数字 */}
              <div
                className={`text-xs sm:text-sm font-medium leading-none ${
                  isToday ? "text-blue-600" : "text-gray-800"
                }`}
              >
                {day}
              </div>

              {/* 支出金额 */}
              {hasExpense && (
                <div className="text-xs font-medium text-red-600 leading-none mt-0.5">
                  {formatAmount(dayExpense)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyExpensesCalendar;
